import { colors, fonts } from '@App/static/styles/global'
import styled, { css } from 'styled-components'

export interface ButtonProps {
    id: string
    label: string
    typeOfBtn?: 'filled' | 'outlined' | 'borderless'
    color: string
    size: 'large' | 'medium' | 'small'
    icon?: React.ReactNode
    onClick?: () => void
}

/**
 * @param {string} size
 * @return {void}
 */
const getSizeStyles = (size: ButtonProps['size']) => {
    switch (size) {
        case 'small':
            return css`
                ${fonts.jotaBodySmall()};
                height: 32px;
                padding: 6px 10px;
                font-weight: bold;
                line-height: 0;
            `
        case 'large':
            return css`
                ${fonts.jotaOverlineLarge()};
                height: 60px;
                padding: 18px 24px;
            `
        default:
            return css`
                ${fonts.jotaOverline()};
                height: 40px;
                padding: 8px 16px;
            `
    }
}

/**
 * @param {string} color
 * @return {void}
 */
const getActiveStyles = (color?: string) => {
    if (color === colors.gray.jotaGrayDarker) {
        return css`
            background-color: ${colors.black.jotaBlack};
            border-color: ${colors.gray.jotaGrayLight};
            color: white;
        `
    }
}

/**
 * Componente de botão estilizado com base nas props de tamanho, cor e tipo.
 *
 * @param {string} size - Define o tamanho do botão ('small', 'medium', 'large').
 * @param {string} color - Cor base do botão.
 * @param {string} typeOfBtn - Tipo do botão ('outlined' ou 'filled').
 * @return {StyledComponent} Botão estilizado com estilos dinâmicos.
 */
export const StyledButton = styled.button<
    Pick<ButtonProps, 'size' | 'color' | 'typeOfBtn'>
>`
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ size }) => getSizeStyles(size)}
    ${({ color }) => color && `background-color: ${color};`}
    ${({ typeOfBtn, color }) =>
        typeOfBtn === 'outlined' &&
        css`
            background-color: transparent;
            border: 2px solid ${color};
            color: ${color};
        `}
     &:hover {
        ${({ typeOfBtn, color }) => {
            if (typeOfBtn === 'outlined') {
                return css`
                    background-color: ${color};
                    color: white;
                `
            } else {
                return css``
            }
        }}
    }
    &:active {
        ${({ color }) => getActiveStyles(color)}
    }
`
